import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, buildQueryString } from '@/lib/api'

/**
 * Phase 2 vendor unification — admin vendors hooks.
 *
 * Backend: apps/api/src/routes/admin/vendors.routes.ts (mounted at
 * /api/admin/vendors). Plan: 2026-05-04-vendor-unification-phase2.md Task 23.
 *
 * Pattern mirrors `useAdminVulnerabilities.ts` — `vi.mock('@/lib/api', ...)`
 * test wiring, lower-cased query path, shared cache-key factory.
 */

// ─── Constants ────────────────────────────────────────────────────────────────

export const VENDOR_KINDS = [
  'hyperscaler',
  'identity',
  'security_product',
  'saas',
  'consultancy',
  'msp',
  'other',
] as const
export type VendorKind = (typeof VENDOR_KINDS)[number]

export type VendorAliasSource = 'display' | 'canonical' | 'manual'

// ─── Response shapes — match the API verbatim ────────────────────────────────

export interface AdminVendorListRow {
  id: string
  slug: string
  name: string
  vendorKind: VendorKind
  vendorTags: string[] | null
  countryIso: string | null
  isActive: boolean
  movedToPersonId: string | null
  description: string | null
  homepageUrl: string | null
  notes: string | null
  createdAt: string | null
}

export interface AdminVendorListResponse {
  page: number
  limit: number
  total: number
  rows: AdminVendorListRow[]
}

export interface AdminVendorAlias {
  id: string
  alias: string
  aliasNormalised: string
  source: VendorAliasSource
  createdAt: string | null
}

export interface AdminVendorDetail extends AdminVendorListRow {
  aliasCount: number
  advisoryFeedCount: number
  statusPageCount: number
  aliases: AdminVendorAlias[]
}

export interface AdminVendorListFilters {
  q?: string
  kind?: VendorKind
  tag?: string
  isActive?: 'true' | 'false'
  country?: string
  page?: number
  limit?: number
}

export interface CreateVendorBody {
  name: string
  slug: string
  vendorKind: VendorKind
  vendorTags?: string[]
  countryIso?: string
  description?: string
  homepageUrl?: string
  notes?: string
  aliases?: string[]
}

export type PatchVendorBody = Partial<{
  name: string
  slug: string
  vendorKind: VendorKind
  vendorTags: string[]
  countryIso: string | null
  description: string | null
  homepageUrl: string | null
  notes: string | null
}>

export interface MoveVendorToPersonBody {
  displayName?: string
}

export type MoveVendorToPersonResponse =
  | { moved: true; personId: string }
  | { already_moved: true; person_id: string }

export interface VendorAliasListResponse {
  ok: true
  aliases: AdminVendorAlias[]
}

// ─── Cache keys ──────────────────────────────────────────────────────────────

export const adminVendorsKeys = {
  all: ['admin-vendors'] as const,
  list: (filters: AdminVendorListFilters) =>
    [...adminVendorsKeys.all, 'list', filters] as const,
  detail: (id: string) => [...adminVendorsKeys.all, 'detail', id] as const,
}

// ─── Hooks ───────────────────────────────────────────────────────────────────

/** GET /api/admin/vendors */
export function useAdminVendorsList(filters: AdminVendorListFilters = {}) {
  return useQuery<AdminVendorListResponse>({
    queryKey: adminVendorsKeys.list(filters),
    queryFn: () =>
      api.get<AdminVendorListResponse>(
        `/admin/vendors${buildQueryString({ ...filters })}`,
      ),
    staleTime: 10_000,
  })
}

/** GET /api/admin/vendors/:id */
export function useAdminVendor(id: string | undefined) {
  return useQuery<AdminVendorDetail>({
    queryKey: id
      ? adminVendorsKeys.detail(id)
      : [...adminVendorsKeys.all, 'detail', 'none'],
    queryFn: () => api.get<AdminVendorDetail>(`/admin/vendors/${id}`),
    enabled: !!id,
    staleTime: 10_000,
  })
}

/** POST /api/admin/vendors */
export function useCreateVendor() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateVendorBody) =>
      api.post<{ ok: true; id: string }>('/admin/vendors', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminVendorsKeys.all })
    },
  })
}

/** PATCH /api/admin/vendors/:id */
export function useEditVendor(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: PatchVendorBody) =>
      api.patch<{ ok: true; id: string }>(`/admin/vendors/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminVendorsKeys.all })
      qc.invalidateQueries({ queryKey: adminVendorsKeys.detail(id) })
    },
  })
}

/** DELETE /api/admin/vendors/:id — soft delete (is_active = false). */
export function useSoftDeleteVendor() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<{ ok: true; id: string; softDeleted: true }>(
        `/admin/vendors/${id}`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminVendorsKeys.all })
    },
  })
}

/** POST /api/admin/vendors/:id/move-to-persons */
export function useMoveVendorToPerson() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body?: MoveVendorToPersonBody }) =>
      api.post<MoveVendorToPersonResponse>(
        `/admin/vendors/${id}/move-to-persons`,
        body ?? {},
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminVendorsKeys.all })
      qc.invalidateQueries({ queryKey: ['admin-persons'] })
    },
  })
}

/** POST /api/admin/vendors/:id/aliases */
export function useAddAlias() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ vendorId, alias }: { vendorId: string; alias: string }) =>
      api.post<VendorAliasListResponse>(
        `/admin/vendors/${vendorId}/aliases`,
        { alias },
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: adminVendorsKeys.detail(vars.vendorId) })
    },
  })
}

/** DELETE /api/admin/vendors/:id/aliases/:aliasId */
export function useRemoveAlias() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      vendorId,
      aliasId,
    }: {
      vendorId: string
      aliasId: string
    }) =>
      api.delete<VendorAliasListResponse>(
        `/admin/vendors/${vendorId}/aliases/${aliasId}`,
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: adminVendorsKeys.detail(vars.vendorId) })
    },
  })
}
